import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import { AdministratorInCamelCase, Company } from 'mbs-ui-shared@models';
import Computer, { AgentType, OsType } from 'mbs-ui-shared@models/Computer';
import { Tag } from 'mbs-ui-shared@models/tag';
import { SelectAdminUtilityComponent } from './components/select-admin-utility/select-admin-utility.component';
import { SelectCompanyUtilityComponent } from './components/select-company-utility/select-company-utility.component';
import { SelectComputerUtilityComponent } from './components/select-computer-utility/select-computer-utility.component';
import { SelectTagUtilityComponent } from './components/select-tag-utility/select-tag-utility.component';
import { UtilityModalSelectEntityType } from './const';

export const childComponentImport = [
  SelectComputerUtilityComponent,
  SelectAdminUtilityComponent,
  SelectCompanyUtilityComponent,
  SelectTagUtilityComponent
];

@UntilDestroy()
@Component({
  selector: 'mbs-utility-selection-modal',
  templateUrl: './utility-selection-modal.component.html',
  standalone: true,
  imports: [CommonModule, ButtonComponent, ...childComponentImport, I18NextPipe, ModalComponent, ModalFooterDirective]
})
export class UtilitySelectionModalComponent implements OnInit {
  private el = inject(ElementRef);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public modalTitle: string;
  public noDataText: string;
  public saveButtonText: string;
  public applyTo: UtilityModalSelectEntityType;
  public showOffline: boolean;
  public osType: OsType;
  public multipleSelect = false;
  public disabledIds: string[] = [];
  public disabledHint: string = null;
  public showAddNewCompany = false;
  public allowEmptySelection = false;
  public allFilters = false;
  public agentType: AgentType = null;

  // specified use case for computer build changes
  public isSpecificCase = false;
  public optionalOrderingParams = null;
  public selectedValue = null;

  public selectedAdministratorIds: string[] = [];
  public selectedCompanyIds: string[] = [];
  public selectedComputerIds: string[] = [];
  public selectedTagIds: number[] = [];

  public selectedAdministrators: AdministratorInCamelCase[] = [];
  public selectedCompanies: Company[] = [];
  public selectedComputers: Computer[] = [];
  public selectedTags: Tag[] = [];

  public applyToEnum = UtilityModalSelectEntityType;
  public showSaveBtn = false;
  public loading = true;

  ngOnInit(): void {
    this.modalTitle = this.baseModal.data.modalTitle;
    this.noDataText = this.baseModal.data.noDataText;
    this.saveButtonText = this.baseModal.data.saveButtonText;
    this.applyTo = this.baseModal.data.applyTo;
    this.showOffline = this.baseModal?.data?.showOffline ?? true;
    this.allFilters = this.baseModal?.data?.allFilters ?? false;
    this.osType = this.baseModal.data?.osType ?? null;
    this.multipleSelect = this.baseModal.data?.multipleSelect ?? false;
    this.disabledIds = this.baseModal.data?.disabledIds ?? [];
    this.disabledHint = this.baseModal.data?.disabledHint ?? null;
    this.showAddNewCompany = this.baseModal.data?.showAddNewCompany ?? false;
    this.allowEmptySelection = this.baseModal.data?.allowEmptySelection ?? false;
    this.showSaveBtn = this.allowEmptySelection;
    this.agentType = this.baseModal.data?.agentType ?? null;

    this.isSpecificCase = this.baseModal.data?.isSpecificCase ?? false;
    this.optionalOrderingParams = this.baseModal.data?.optionalOrderingParams ?? null;
    this.selectedValue = this.baseModal.data?.selectedValue ?? null;

    if (this.applyTo === UtilityModalSelectEntityType.administrators) this.selectedAdministratorIds = this.baseModal.data.model ?? [];
    if (this.applyTo === UtilityModalSelectEntityType.companies) this.selectedCompanyIds = this.baseModal.data.model ?? [];
    if (this.applyTo === UtilityModalSelectEntityType.computers) this.selectedComputerIds = this.baseModal.data.model ?? [];
    if (this.applyTo === UtilityModalSelectEntityType.tags) this.selectedTagIds = this.baseModal.data.model ?? [];
  }

  close(): void {
    this.baseModal.close();
  }

  setComputerSelection(data: Computer[]) {
    this.showSaveBtn = !!data.length;
    this.selectedComputers = data;
  }

  setCompanySelection(data: Company[]) {
    this.showSaveBtn = this.allowEmptySelection || !!data.length;
    this.selectedCompanies = data;
  }

  setAdministratorSelection(data: AdministratorInCamelCase[]) {
    this.showSaveBtn = !!data.length;
    this.selectedAdministrators = data;
  }

  setTagSelection(data: Tag[]) {
    this.showSaveBtn = true;
    this.selectedTags = data;
  }

  save(): void {
    switch (this.applyTo) {
      case UtilityModalSelectEntityType.companies:
        this.baseModal.save(this.selectedCompanies);
        break;
      case UtilityModalSelectEntityType.computers:
        this.baseModal.save(this.selectedComputers);
        break;
      case UtilityModalSelectEntityType.administrators:
        this.baseModal.save(this.selectedAdministrators);
        break;
      case UtilityModalSelectEntityType.tags:
        this.baseModal.save(this.selectedTags);
        break;
      default:
        this.close();
    }
  }

  haveSelectedData() {
    switch (this.applyTo) {
      case UtilityModalSelectEntityType.companies:
        return !!this.selectedCompanies.length || !!this.selectedCompanyIds.length;
      case UtilityModalSelectEntityType.computers:
        return !!this.selectedComputers || !!this.selectedComputerIds.length;
      case UtilityModalSelectEntityType.administrators:
        return !!this.selectedAdministrators.length || !!this.selectedAdministratorIds.length;
      case UtilityModalSelectEntityType.tags:
        return !!this.selectedTags.length || !!this.selectedTagIds.length;
      default:
        return false;
    }
  }
}
